<template>
  <div class="cpd-provider-page">
    <v-card class="cpd-provider-card">
      <div class="cpd-provider-header">
        <div class="cpd-provider-header__copy">
          <div class="cpd-provider-title">CPD Providers</div>
          <div class="cpd-provider-subtitle">Manage CPD provider records and renewals.</div>
        </div>
        <v-text-field
            v-model="search"
            class="cpd-provider-search"
            append-icon="mdi-magnify"
            hide-details
            label="Search"
            single-line
        ></v-text-field>
        <v-btn
            v-if="canCreateTrainingCenter"
            class="cpd-provider-add-button"
            depressed
            @click="addTrainingCenter()"
        >
          <v-icon left>mdi-plus-circle</v-icon>
          <span>Add CPD provider</span>
        </v-btn>
      </div>
      <v-data-table
          :headers="headers"
          :items="trainingCenters.rows"
          :loading="loading"
          :options.sync="options"
          :server-items-length="trainingCenters.count"
          class="cpd-provider-table elevation-1"
          loading-text="Loading... please wait"
      >
        <template v-slot:[`item.trainingCenterName`]="{ item }">
          {{ item.trainingCenterName ? item.trainingCenterName : "-" }}
        </template>
        <template v-slot:[`item.geolocation`]="{ item }">
          {{ item.geolocation ? item.geolocation : "-" }}
        </template>
        <template v-slot:[`item.cpdProviderOwnership`]="{ item }">
          {{ item.cpdProviderOwnership ? item.cpdProviderOwnership : "-" }}
        </template>
        <template v-slot:[`item.isActive`]="{ item }">
          <v-chip :color="getColor(item)" dark>
            {{ getStatusLabel(item) }}
          </v-chip>
        </template>
        <template v-slot:[`item.checklist`]="{ item }">
          <v-btn
              v-if="item?.checklist"
              icon
              class="cpd-provider-download-button"
              @click="downloadFile(item.checklist)"
          >
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="cpd-provider-action-group">
            <v-tooltip bottom>
              <template v-if="canViewTrainingCenter" v-slot:activator="{ on }">
                <v-icon
                    class="cpd-provider-action-icon"
                    @click="viewTrainingCenter(item)"
                    v-on="on"
                >mdi-eye
                </v-icon
                >
              </template>
              <span>View CPD Provider</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-if="canEditTrainingCenter(item)" v-slot:activator="{ on }">
                <v-icon
                    class="cpd-provider-action-icon"
                    @click="editTrainingCenter(item)"
                    v-on="on"
                >mdi-circle-edit-outline
                </v-icon
                >
              </template>
              <span>Edit CPD Provider</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-if="canRenewCpdProvider(item)" v-slot:activator="{ on }">
                <v-icon
                    class="cpd-provider-action-icon"
                    @click="renewAccreditor(item)"
                    v-on="on"
                >mdi-autorenew
                </v-icon
                >
              </template>
              <span>Renew Cpd Provider</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-if="canTransferCpd(item)" v-slot:activator="{ on }">
                <v-icon
                    class="cpd-provider-action-icon"
                    @click="transferCpd(item)"
                    v-on="on"
                >mdi-account-convert
                </v-icon
                >
              </template>
              <span>Cpd Transfer</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <template>
      <v-dialog v-model="dialog" persistent width="60%">
        <v-card class="cpd-provider-dialog-card">
          <v-card-title
              class="cpd-provider-dialog__title"
              primary-title
          >{{ title }}

            <v-spacer></v-spacer>

            <v-btn class="cpd-provider-dialog__close" icon @click="closeDialog()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title
          >
          <v-divider></v-divider>
          <HrdAddTrainingCenter
              v-if="dialog"
              :trainingCenter="trainingCenter"
              @close="closeDialog()"
              @registerOrUpdateTrainingCenter="registerOrUpdateTrainingCenter"
          ></HrdAddTrainingCenter>
        </v-card>
      </v-dialog>
    </template>
    <template>
      <v-dialog v-model="detailDialog" persistent width="60%">
        <v-card class="cpd-provider-dialog-card">
          <v-card-title
              class="cpd-provider-dialog__title"
              primary-title
          >{{ title }}

            <v-spacer></v-spacer>

            <v-btn class="cpd-provider-dialog__close" icon @click="closeDialog()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title
          >
          <v-divider></v-divider>
          <HrdTrainingCenterDetail
              v-if="detailDialog"
              :trainingCenter="trainingCenter"
          ></HrdTrainingCenterDetail>
        </v-card>
      </v-dialog>
    </template>
    <template>
      <v-dialog v-model="renewalDialog" persistent width="60%">
        <v-card class="cpd-provider-dialog-card">
          <v-card-title
              class="cpd-provider-dialog__title"
              primary-title
          >{{ title }}

            <v-spacer></v-spacer>

            <v-btn class="cpd-provider-dialog__close" icon @click="closeDialog()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title
          >

          <v-divider></v-divider>
          <v-card class="cpd-provider-dialog__content" outlined>
            <div class="cpd-provider-dialog__form">
              <div class="cpd-provider-dialog__field required">
                <label class="cpd-provider-dialog__label">Renew Date</label>
                <validation-provider v-slot="{ errors }" rules="required">
                  <v-menu
                      ref="menu"
                      v-model="startMenu"
                      :close-on-content-click="false"
                      max-width="290px"
                      min-width="290px"
                      offset-y
                      transition="scale-transition"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                          v-model="formValues.accreditedDate"
                          label="Renew date"
                          persistent-hint
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                        ref="accreditationDate"
                        v-model="formValues.accreditedDate"
                        :max="date"
                        no-title
                        scrollable
                        @input="startMenu = false"
                    ></v-date-picker>
                  </v-menu>
                  <span class="text-gray-600 text-xs italic text-red-700">{{
                      errors[0]
                    }}</span>
                </validation-provider>
              </div>
              <div class="cpd-provider-dialog__action">
                <v-btn
                    class="cpd-provider-dialog__primary"
                    elevation="1"
                    small
                    @click="addRenewal()"
                >
                  <v-icon left small>mdi-autorenew</v-icon>
                  Renew
                </v-btn>
              </div>
            </div>
            <v-divider class="cpd-provider-dialog__divider"></v-divider>
            <v-data-table
                :headers="renewalHeader"
                :items="renewals"
                :loading="loading"
                :server-items-length="renewals.length"
                class="cpd-provider-dialog__table elevation-1"
                loading-text="Loading... please wait"
            ></v-data-table>
          </v-card>
        </v-card>
      </v-dialog>
    </template>
    <template>
      <v-dialog v-model="transferDialog" persistent width="60%">
        <v-card class="cpd-provider-dialog-card">
          <v-card-title
              class="cpd-provider-dialog__title"
              primary-title
          >{{ title }}

            <v-spacer></v-spacer>

            <v-btn class="cpd-provider-dialog__close" icon @click="closeDialog()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title
          >

          <v-divider></v-divider>
          <v-card class="cpd-provider-dialog__content" outlined>
            <div class="cpd-provider-dialog__form cpd-provider-dialog__form--transfer">
              <div class="cpd-provider-dialog__field required">
                <label class="cpd-provider-dialog__label">Transferred To</label>
                <validation-provider v-slot="{ errors }" rules="required">
                  <v-autocomplete
                      v-model="transferFormValues.transferredTo"
                      :clearable="clearable"
                      :disabled="disable"
                      :elevation="1"
                      :items="accreditors"
                      :loading="accreditorsLoading"
                      :searchable="searchable"
                      dense
                      hint="search for accrediator"
                      item-text="accreditorName"
                      item-value="id"
                      label="accreditors"
                      persistent-hint
                      solo
                  ></v-autocomplete>

                  <span class="text-gray-600 text-xs italic text-red-700">{{
                      errors[0]
                    }}</span>
                </validation-provider>
              </div>
              <div class="cpd-provider-dialog__field required">
                <label class="cpd-provider-dialog__label">Transferred Date</label>
                <validation-provider v-slot="{ errors }" rules="required">
                  <v-menu
                      ref="menu"
                      v-model="startMenu"
                      :close-on-content-click="false"
                      max-width="290px"
                      min-width="290px"
                      offset-y
                      transition="scale-transition"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                          v-model="transferFormValues.transferredDate"
                          label="Transferred date"
                          persistent-hint
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                        ref="accreditationDate"
                        v-model="transferFormValues.transferredDate"
                        :max="date"
                        no-title
                        scrollable
                        @input="startMenu = false"
                    ></v-date-picker>
                  </v-menu>
                  <span class="text-gray-600 text-xs italic text-red-700">{{
                      errors[0]
                    }}</span>
                </validation-provider>
              </div>
              <div class="cpd-provider-dialog__action cpd-provider-dialog__action--full">
                <v-btn
                    class="cpd-provider-dialog__primary"
                    elevation="1"
                    small
                    @click="addTransfer()"
                >
                  <v-icon left small>mdi-account-convert</v-icon>
                  Transfer
                </v-btn>
              </div>
            </div>
            <v-divider class="cpd-provider-dialog__divider"></v-divider>
            <v-data-table
                :headers="transferHeader"
                :items="transfers"
                :loading="loading"
                :server-items-length="transfers.length"
                class="cpd-provider-dialog__table elevation-1"
                loading-text="Loading... please wait"
            ></v-data-table>
          </v-card>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>

<script>
import {accessPolicy} from "@/utils/managedAccess";
import HrdAddTrainingCenter from "./hrd-add-training-center";
import HrdTrainingCenterDetail from "./hrd-training-center-detail";
import {TrainingCenterService} from "./training_center.service";
import {getUser} from "@/utils/helper";
import {ValidationProvider, extend} from "vee-validate";
import {required} from "vee-validate/dist/rules";
import {AccreditorService} from "@/components/Accreditors/accreditor.service";
import {openPresignedUrl} from "@/utils/file";

extend("required", {
  ...required,
  message: "This field is required",
});

export default {
  components: {
    HrdAddTrainingCenter,
    HrdTrainingCenterDetail,
    ValidationProvider
  },

  data() {
    return {
      dialog: false,
      detailDialog: false,
      title: "",
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      search: "",
      searchDebounce: null,
      trainingCenters: {rows: [], count: 0},
      headers: [
        {text: "CPD Provider Name", value: "trainingCenterName"},
        {text: "Location", value: "region.name"},
        {text: "CPD Provider's ownership", value: "cpdProviderOwnership"},
        {text: "CPD Provider's Status", value: "isActive", align: "center"},
        {text: "Assessment Checklist", value: "checklist", align: "center"},
        {text: "Actions", value: "actions", sortable: false, align: "center"},
      ],
      loading: false,
      trainingCenter: null,
      accreditor: null,

      renewalDialog: false,
      transferDialog: false,

      startMenu: false,
      startMenu2: false,
      date: new Date().toISOString().substr(0, 10),
      formValues: {
        accreditedDate: "",
        accreditedBy: null,
        cpdId: null,
        accreditorId: null,
      },
      renewalHeader: [
        {text: "Previous Accredited Date", value: "accreditedDate"},
        {text: "Accredited By", value: "user.firstName"},
      ],
      renewals: [],


      transferFormValues: {
        transferredDate: "",
        transferredBy: null,
        transferredFrom: null,
        transferredTo: null,
        cpdId: null,
        createdBy: null,
      },
      accreditors: [],
      transferHeader: [
        {text: "Transferred From", value: "accreditorFrom.accreditorName"},
        {text: "Transferred To", value: "accreditorTo.accreditorName"},
        {text: "Transferred Date", value: "transferredDate"},
        {text: "Transferred By", value: "user.firstName"},
      ],
      transfers: [],
    };
  },
  async beforeMount() {
    this.fetchTrainingCenters();
    const res = await AccreditorService.getAllAccreditorsList();
    if (res?.status === 200 || res?.data?.rows) {
      this.accreditors = res?.data?.rows || res?.rows || [];
    } else {
      this.$toast.error(
          "There was a problem fetching Accreditors, Please try Again!"
      );
    }
  },
  watch: {
    options: {
      handler() {
        this.fetchTrainingCenters();
      },
      deep: true,
    },
    search: function (val) {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = null;
      }
      const text = (val || "").toString().trim();
      if (text.length === 0) {
        this.fetchTrainingCenters();
        return;
      }
      if (text.length < 3) {
        return;
      }
      this.searchDebounce = setTimeout(() => {
        this.fetchTrainingCenters();
      }, 400);
    },
  },
  computed: {
    currentUser() {
      return getUser() || {permissions: [], data: {}};
    },
    userData() {
      return this.currentUser?.data || {};
    },
    userPermissions() {
      return this.currentUser?.permissions || [];
    },
    isReadOnly() {
      return Boolean(this.currentUser?.data?.readOnly);
    },
    isSystemAdmin() {
      return accessPolicy(this.currentUser) ? accessPolicy(this.currentUser).organization?.kind === "ministry" : this.userData?.userRoles?.roleName === "System admins";
    },
    canCreateTrainingCenter() {
      return !this.isReadOnly && this.hasCpdPermission("canCreate");
    },
    canViewTrainingCenter() {
      return this.hasCpdPermission("canRead");
    },
  },
  methods: {
    hasCpdPermission(permissionKey) {
      return this.userPermissions.some(
          (permission) =>
              permission?.menuItem?.menuItemName === "CPD Provider" &&
              permission?.[permissionKey]
      );
    },
    async fetchTrainingCenters() {
      this.loading = true;
      const {page, itemsPerPage} = this.options;
      try {
        const res = await TrainingCenterService.getTrainingCentersWP(
            page,
            itemsPerPage,
            this.search
        );
        if (res?.status === 200) {
          const payload = res?.data?.data ?? res?.data;
          const rows = Array.isArray(payload?.rows)
              ? payload.rows
              : Array.isArray(payload)
                  ? payload
                  : [];
          const count = Number.isFinite(payload?.count) ? payload.count : rows.length;
          this.trainingCenters = {rows, count};
        } else {
          this.$toast.error(
              res?.data?.message ||
              "There was a problem fetching CPD Providers, Please try Again!"
          );
        }
      } catch (error) {
        this.$toast.error(
            "There was a problem fetching CPD Providers, Please try Again!"
        );
      } finally {
        this.loading = false;
      }
    },
    closeDialog() {
      this.dialog = false;
      this.detailDialog = false;
      this.renewalDialog = false;
      this.transferDialog = false;
      this.fetchTrainingCenters();
    },
    viewTrainingCenter(item) {
      this.trainingCenter = item;
      this.detailDialog = true;
      this.title = "CPD Provider Detail";
    },
    addTrainingCenter() {
      this.trainingCenter = null;
      this.dialog = true;
      this.title = "CPD Provider";
    },
    isAccreditorAssignedToProvider(item) {
      if (item?.cpdProviderAccess) {
        return Boolean(item.cpdProviderAccess.isAssignedToMyAccreditor);
      }
      const currentAccreditorId = Number(this.userData?.accreditorId);
      const providerAccreditorId = Number(item?.accreditedById);

      if (!currentAccreditorId || Number.isNaN(currentAccreditorId)) {
        return false;
      }

      return providerAccreditorId === currentAccreditorId;
    },
    canEditTrainingCenter(item) {
      if (!this.$can("provider","edit")) return false;
      if (item?.cpdProviderAccess) {
        return !this.isReadOnly && Boolean(item.cpdProviderAccess.canModify);
      }
      const isOwner =
          this.userData?.id === item?.createdById || this.userData?.cpdProviderId === item?.id;
      const canEditOwnedProvider = this.hasCpdPermission("canUpdate") && isOwner;
      const canEditAsAccreditor = this.isAccreditorAssignedToProvider(item);

      return !this.isReadOnly && (this.isSystemAdmin || canEditOwnedProvider || canEditAsAccreditor);
    },
    editTrainingCenter(item) {
      this.trainingCenter = item;
      this.dialog = true;
      this.title = "CPD Provider";
    },
    async registerOrUpdateTrainingCenter(trainingCenter) {
      const userId = this.userData?.id;
      if (!userId) {
        this.$toast.error("Unable to identify current user. Please login again.");
        return;
      }
      if (trainingCenter.get('editMode') == 'true') {
        try {
          const res = await TrainingCenterService.update(
              Number(trainingCenter.get('id')),
              trainingCenter
          );
          if ((res?.status === 200 || res?.status === 201) && res?.data?.status === 200) {
            this.$toast.success(res?.data?.message || "CPD Provider updated successfully");
            await this.fetchTrainingCenters();
          } else {
            this.$toast.error(
                res?.data?.message || "Unable to update CPD Provider, please try again"
            );
          }
        } catch (error) {
          this.$toast.error("Unable to update CPD Provider, please try again");
        }
        return;
      }

      try {
        trainingCenter.append("createdById", userId);
        const res = await TrainingCenterService.createFormData(trainingCenter);
        if (res?.status === 200 || res?.status === 201) {
          switch (res.data?.status) {
            case 200:
              this.$toast.success(res.data.message);
              this.formValues.cpdId = res.data.data.id;
              this.formValues.accreditedDate = res.data.data.accreditationDate;
              await AccreditorService.addRenewal(this.formValues);
              await this.fetchTrainingCenters();
              break;
            case 500:
              this.$toast.error(res.data.message);
              break;
            default:
              this.$toast.error("Unable to create CPD Provider, please try again");
          }
        } else {
          this.$toast.error("Unable to create CPD Provider, please try again");
        }
      } catch (error) {
        this.$toast.error("Unable to create CPD Provider, please try again");
      }
    },
    async fetchRenewalList(id) {
      this.loading = true;
      try {
        const res = await TrainingCenterService.getRenewals(id);
        if (res?.status === 200) {
          this.renewals = res.data?.data || res.data || [];
        } else {
          this.$toast.error(
              "There was a problem fetching Renewals, Please try Again!"
          );
        }
      } catch (error) {
        this.$toast.error(
            "There was a problem fetching Renewals, Please try Again!"
        );
      } finally {
        this.loading = false;
      }
    },
    async fetchTransferList(id) {
      this.loading = true;
      try {
        const res = await TrainingCenterService.getTransfers(id);
        if (res?.status === 200) {
          this.transfers = res.data?.data || res.data || [];
        } else {
          this.$toast.error(
              "There was a problem fetching Transfers, Please try Again!"
          );
        }
      } catch (error) {
        this.$toast.error(
            "There was a problem fetching Transfers, Please try Again!"
        );
      } finally {
        this.loading = false;
      }
    },
    canRenewCpdProvider(item) {
      if (!this.$can("provider","renew")) return false;
      if (item?.cpdProviderAccess) {
        return !this.isReadOnly && (this.isSystemAdmin || item.cpdProviderAccess.isAssignedToMyAccreditor);
      }
      return !this.isReadOnly && (this.isSystemAdmin || this.isAccreditorAssignedToProvider(item));
    },
    canTransferCpd(item) {
      if (!this.$can("provider","transfer")) return false;
      if (item?.cpdProviderAccess) {
        return !this.isReadOnly && (this.isSystemAdmin || item.cpdProviderAccess.isAssignedToMyAccreditor);
      }
      return !this.isReadOnly && (this.isSystemAdmin || this.isAccreditorAssignedToProvider(item));
    },
    async downloadFile(path) {
      await openPresignedUrl(path, {preferHttps: false, allowInsecure: true, toast: this.$toast});
    },
    renewAccreditor(item) {
      this.accreditor = item;
      this.title = "Renew Cpd Provider";
      this.renewalDialog = true;
      const today = this.getTodayDate();
      this.formValues = {
        accreditedDate: today,
        accreditedBy: null,
        cpdId: item.id,
        accreditorId: null,
      };
      this.fetchRenewalList(item.id);
    },
    async transferCpd(item) {
      this.accreditor = item;
      this.title = "Transfer CPD";
      this.transferDialog = true;
      this.transferFormValues = {
        transferredDate: this.getTodayDate(),
        transferredBy: null,
        transferredFrom: item?.cpdProviderAccess?.effectiveAccreditorId || item.accreditedById,
        transferredTo: null,
        cpdId: item.id,
        createdBy: item.createdById,
      };
      this.fetchTransferList(item.id);
    },
    async addRenewal() {
      try {
        if (!this.formValues.accreditedDate) {
          this.formValues.accreditedDate = this.getTodayDate();
        }
        const res = await TrainingCenterService.addRenewal(this.formValues);
        if (res?.status === 200) {
          this.$toast.success(res.data?.message);
          this.closeDialog();
        } else {
          this.$toast.error(res?.data?.message || "Unable to renew CPD Provider, please try again");
        }
      } catch (error) {
        this.$toast.error("Unable to renew CPD Provider, please try again");
      }
    },
    async addTransfer() {
      try {
        const res = await TrainingCenterService.addTransfer(this.transferFormValues);
        if (res?.status === 200) {
          this.$toast.success(res.data?.message);
          this.closeDialog();
        } else {
          this.$toast.error(res?.data?.message || "Unable to transfer CPD Provider, please try again");
        }
      } catch (error) {
        this.$toast.error("Unable to transfer CPD Provider, please try again");
      }
    },
    getStatusLabel(item) {
      if (item?.statusLabel) {
        return item.statusLabel;
      }
      return item?.isActive === false ? "Not Active" : "Active";
    },
    getColor(item) {
      const status = this.getStatusLabel(item);
      if (status === "Expired") return "orange darken-2";
      if (status === "Not Active") return "red";
      return "green";
    },
    getTodayDate() {
      return new Date().toISOString().substr(0, 10);
    },
  },
};
</script>
<style scoped>
.cpd-provider-page {
  padding: 24px;
  background: #f5f7fb;
  min-height: 100%;
}

.cpd-provider-card {
  border-radius: 0 0 12px 12px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #ffffff;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.cpd-provider-header {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(240px, 360px) auto;
  align-items: center;
  gap: 16px;
  padding: 24px 28px;
}

.cpd-provider-header__copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cpd-provider-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.cpd-provider-subtitle {
  font-size: 14px;
  color: #475569;
}

.cpd-provider-search {
  width: 100%;
  max-width: 360px;
  justify-self: center;
  align-self: center;
}

.cpd-provider-table {
  border-radius: 0 0 12px 12px;
}

.cpd-provider-dialog-card {
  border-radius: 10px;
  overflow: hidden;
}

.cpd-provider-dialog__title {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #3a4f63;
  color: #ffffff;
  padding: 20px 24px;
}

.cpd-provider-dialog__close {
  color: #ffffff !important;
}

.cpd-provider-dialog__close .v-icon {
  color: #ffffff !important;
}

.cpd-provider-add-button {
  background: #1976d2 !important;
  border-color: #1976d2 !important;
  color: #ffffff !important;
  text-transform: none;
  font-weight: 600;
}

.cpd-provider-action-icon {
  color: #1976d2 !important;
}

.cpd-provider-download-button .v-icon {
  color: #1976d2 !important;
}

.cpd-provider-action-group {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: nowrap;
  white-space: nowrap;
}

.cpd-provider-action-group .v-tooltip {
  display: inline-flex;
}

.cpd-provider-dialog__content {
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
}

.cpd-provider-dialog__form {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) auto;
  gap: 16px;
  align-items: end;
}

.cpd-provider-dialog__form--transfer {
  grid-template-columns: repeat(2, minmax(220px, 1fr));
}

.cpd-provider-dialog__field {
  min-width: 0;
}

.cpd-provider-dialog__label {
  display: block;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 6px;
}

.cpd-provider-dialog__action {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
}

.cpd-provider-dialog__action--full {
  grid-column: 1 / -1;
}

.cpd-provider-dialog__primary {
  background: #1976d2 !important;
  border-color: #1976d2 !important;
  color: #ffffff !important;
  text-transform: none;
  font-weight: 600;
}

.cpd-provider-dialog__divider {
  margin: 16px 0;
}

.cpd-provider-dialog__table {
  background: #ffffff;
  border-radius: 10px;
  overflow: hidden;
}

@media (max-width: 700px) {
  .cpd-provider-page {
    padding: 12px;
  }

  .cpd-provider-header {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 20px;
  }

  .cpd-provider-search {
    max-width: 100%;
  }

  .cpd-provider-add-button {
    width: 100%;
    justify-content: center;
  }

  .cpd-provider-dialog__content {
    padding: 12px;
  }

  .cpd-provider-dialog__form {
    grid-template-columns: 1fr;
  }

  .cpd-provider-dialog__action {
    justify-content: flex-start;
  }
}
</style>
